/**
 * WooCommerce Adapter
 * Hooks into WooCommerce events and extracts e-commerce data
 */

import { BaseAdapter } from './base-adapter';
import { ProductData, CartData, OrderData } from '../types';
import { parsePrice } from '../utils/price';

declare const jQuery: any;
declare const wc_add_to_cart_params: any;
declare const wc_cart_fragments_params: any;

export class WooCommerceAdapter extends BaseAdapter {
  
  getName(): string {
    return 'WooCommerce';
  }

  detect(): boolean {
    // Check for WooCommerce JS globals (most reliable)
    const hasWooCommerceJS = 
      typeof wc_add_to_cart_params !== 'undefined' ||
      typeof wc_cart_fragments_params !== 'undefined' ||
      (typeof jQuery !== 'undefined' && jQuery.fn.wc_add_to_cart);
    
    // Check for WooCommerce body classes (if body exists)
    const hasWooCommerceBody = 
      document.body?.classList?.contains('woocommerce') ||
      document.body?.classList?.contains('woocommerce-page');
    
    // Check for WooCommerce URL patterns (works before body is ready)
    const hasWooCommerceUrl = 
      /\/(product|produkt|proizvod|kosarica|cart|checkout|kasse|naplata|placanje|blagajna|zaprimljeno)\//.test(window.location.pathname) ||
      /wc-ajax/.test(window.location.search);
    
    // Check for WooCommerce DOM elements (if any exist)
    const hasWooCommerceDOM = 
      !!document.querySelector('.woocommerce') ||
      !!document.querySelector('[class*="woocommerce"]') ||
      !!document.querySelector('form.cart');
    
    const isWooCommerce = hasWooCommerceJS || hasWooCommerceBody || hasWooCommerceUrl || hasWooCommerceDOM;
    
    
    return isWooCommerce;
  }

  init(): void {
    this.log('Initializing WooCommerce adapter');
  }

  hookAddToCart(callback: (product: ProductData) => void): void {
    // Method 1: Direct click handler on add-to-cart buttons (most reliable)
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const button = target.closest('.add_to_cart_button, .single_add_to_cart_button, button[name="add-to-cart"]');
      
      if (button) {
        this.log('Add to cart button clicked');
        
        // Try to get product data from button attributes or form
        const productData = this.extractProductFromButton(button as HTMLElement) || this.extractProductFromForm();
        if (productData) {
          callback(productData);
        }
      }
    });

    // Method 2: jQuery events (WooCommerce standard)
    if (typeof jQuery !== 'undefined') {
      // Hook into WooCommerce's added_to_cart event
      jQuery(document).on('added_to_cart', (_event: any, _fragments: any, _cart_hash: any, button: any) => {
        this.log('WooCommerce added_to_cart event triggered');

        const productData: ProductData = {
          productId: button?.data?.('product_id')?.toString() || '',
          productSku: button?.data?.('product_sku') || '',
          productName: button?.data?.('product_name') || button?.attr?.('aria-label') || '',
          productPrice: parseFloat(button?.data?.('price')) || 0,
          quantity: parseInt(button?.data?.('quantity')) || 1
        };

        if (productData.productId) {
          callback(productData);
        }
      });

      // Method 3: Monitor AJAX add to cart responses
      jQuery(document).ajaxComplete((_event: any, xhr: any, settings: any) => {
        if (settings.url && settings.url.indexOf('wc-ajax=add_to_cart') !== -1) {
          this.log('AJAX add to cart detected');
          
          try {
            const response = JSON.parse(xhr.responseText);
            if (response && !response.error) {
              const productData = this.extractProductFromForm();
              if (productData) {
                callback(productData);
              }
            }
          } catch (error) {
            this.log('Failed to parse AJAX response', error);
          }
        }
      });
    }

    this.log('Add to cart hooks installed');
  }

  /**
   * Extract product data from add-to-cart button
   */
  private extractProductFromButton(button: HTMLElement): ProductData | null {
    try {
      const productId = button.getAttribute('data-product_id') || 
                        button.getAttribute('value') ||
                        button.closest('form')?.querySelector('[name="product_id"], [name="add-to-cart"]')?.getAttribute('value') || '';
      
      const productSku = button.getAttribute('data-product_sku') || '';
      
      // Try to get name from nearby elements
      const productName = button.getAttribute('data-product_name') ||
                          button.getAttribute('aria-label') ||
                          document.querySelector('.product_title, h1.product-title')?.textContent?.trim() ||
                          button.closest('.product')?.querySelector('.woocommerce-loop-product__title, .product-title')?.textContent?.trim() ||
                          '';
      
      const priceStr = button.getAttribute('data-price') || 
                       document.querySelector('.price .woocommerce-Price-amount bdi, .price .amount')?.textContent?.replace(/[^0-9.,]/g, '') || '';
      const productPrice = parseFloat(priceStr.replace(',', '.')) || 0;
      
      const quantityInput = button.closest('form')?.querySelector('[name="quantity"]') as HTMLInputElement;
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

      if (!productId) return null;

      return {
        productId,
        productSku,
        productName,
        productPrice,
        quantity
      };
    } catch (error) {
      this.log('Failed to extract product from button', error);
      return null;
    }
  }

  hookProductView(callback: (product: ProductData) => void): void {
    
    // Wait for DOM to be ready before checking for product page
    const checkProductPage = (retryCount = 0) => {
      
      // If body doesn't exist yet, retry
      if (!document.body && retryCount < 5) {
        setTimeout(() => checkProductPage(retryCount + 1), 100);
        return;
      }
      
      // First, check if this is definitely NOT a product page
      const isDefinitelyNotProductPage = 
        document.body?.classList?.contains('home') ||
        document.body?.classList?.contains('blog') ||
        document.body?.classList?.contains('archive') ||
        document.body?.classList?.contains('search') ||
        document.body?.classList?.contains('woocommerce-cart') ||
        document.body?.classList?.contains('woocommerce-checkout') ||
        document.body?.classList?.contains('woocommerce-account') ||
        document.body?.classList?.contains('page-template-template-naslovnica'); // Croatian homepage template
      
      if (isDefinitelyNotProductPage) {
        return;
      }
      
      // Check for single product page indicators
      const hasBodyClass = document.body?.classList?.contains('single-product') ||
        document.body?.classList?.contains('product-template-default');
      
      const hasUrlPattern = 
        /\/product\/[^\/]+\/?$/.test(window.location.pathname) ||
        /\/produkt\/[^\/]+\/?$/.test(window.location.pathname) ||
        /\/proizvod\/[^\/]+\/?$/.test(window.location.pathname);
      
      const isProductPage = hasBodyClass || hasUrlPattern;
      
      
      if (isProductPage) {
        this.log('Single product page detected');

        const productData = this.extractProductFromPage();
        if (productData) {
          callback(productData);
        } else {
          this.log('Could not extract product data from page');
        }
      }
    };

    // Always wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => checkProductPage(0));
    } else {
      // DOM already ready, but use setTimeout to ensure body classes are applied
      setTimeout(() => checkProductPage(0), 0);
    }
  }

  hookViewCart(callback: (cart?: CartData) => void): void {
    const checkCartPage = (retryCount = 0) => {
      
      // If body doesn't exist yet, retry
      if (!document.body && retryCount < 5) {
        setTimeout(() => checkCartPage(retryCount + 1), 100);
        return;
      }
      
      const hasBodyClass = document.body?.classList?.contains('woocommerce-cart');
      const hasCartElements = 
        !!document.querySelector('.woocommerce-cart-form') ||
        !!document.querySelector('.wc-cart-fragments') ||
        !!document.querySelector('table.shop_table.cart');
      const hasUrlPattern = 
        /\/cart\/?$/.test(window.location.pathname) ||
        /\/kosarica\/?$/.test(window.location.pathname) || // Croatian
        /\/warenkorb\/?$/.test(window.location.pathname); // German
      
      const isCartPage = hasBodyClass || hasCartElements || hasUrlPattern;
      
      
      if (isCartPage) {
        this.log('Cart page detected');
        const cart = this.parseCartFromCartPage() || this.parseCartFromDOM();
        callback(cart || undefined);
      }
    };

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(() => checkCartPage(0), 0);
    } else {
      document.addEventListener('DOMContentLoaded', () => checkCartPage(0));
    }
  }

  hookCheckout(callback: (cart?: CartData) => void): void {
    
    const checkCheckoutPage = (retryCount = 0) => {
      
      // If body doesn't exist yet, retry
      if (!document.body && retryCount < 5) {
        setTimeout(() => checkCheckoutPage(retryCount + 1), 100);
        return;
      }
      
      // First check if this is an order-received page (should NOT fire checkout)
      const isOrderReceivedPage = 
        document.body?.classList?.contains('woocommerce-order-received') ||
        !!document.querySelector('.woocommerce-order-received') ||
        !!document.querySelector('.woocommerce-thankyou-order-received') ||
        /\/order-received\//.test(window.location.pathname) ||
        /\/narudzba-zaprimljena\//.test(window.location.pathname) || // Croatian
        /\/zaprimljeno\//.test(window.location.pathname) || // Croatian order received
        /\/bestellung-erhalten\//.test(window.location.pathname) || // German
        /[?&]key=wc_order_/.test(window.location.search);
      
      if (isOrderReceivedPage) {
        this.log('Order received page - skipping checkout detection');
        return;
      }
      
      const hasBodyClass = 
        document.body?.classList?.contains('woocommerce-checkout') ||
        document.body?.classList?.contains('checkout');
      const hasCheckoutElements = 
        !!document.querySelector('.woocommerce-checkout') ||
        !!document.querySelector('form.checkout.woocommerce-checkout') ||
        !!document.querySelector('#customer_details') ||
        !!document.querySelector('.woocommerce-billing-fields');
      const hasUrlPattern = 
        /\/checkout\/?$/.test(window.location.pathname) ||
        /\/kasse\/?$/.test(window.location.pathname) || // German
        /\/naplata\/?$/.test(window.location.pathname) || // Croatian
        /\/placanje\/?$/.test(window.location.pathname) || // Croatian alternative
        /\/blagajna\/?$/.test(window.location.pathname); // Croatian checkout
      
      const isCheckoutPage = hasBodyClass || hasCheckoutElements || hasUrlPattern;
      
      
      if (isCheckoutPage) {
        this.log('Checkout page detected');
        const cart = this.parseCartFromCheckout() || this.parseCartFromDOM();
        callback(cart || undefined);
      }
    };

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(() => checkCheckoutPage(0), 0);
    } else {
      document.addEventListener('DOMContentLoaded', () => checkCheckoutPage(0));
    }
  }

  hookPurchase(callback: (order: OrderData) => void): void {
    // Helper to check if order was already tracked (persists across page reloads)
    const STORAGE_KEY = 'wc_tracked_orders';
    const getTrackedOrders = (): string[] => {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    };
    
    const markOrderTracked = (orderId: string): void => {
      try {
        const tracked = getTrackedOrders();
        if (!tracked.includes(orderId)) {
          tracked.push(orderId);
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tracked));
        }
      } catch (error) {
        this.log('Failed to mark order as tracked', error);
      }
    };
    
    const isOrderTracked = (orderId: string): boolean => {
      return getTrackedOrders().includes(orderId);
    };

    // Method 1: Intercept wc-ajax=checkout response (MOST RELIABLE)
    // This captures the order_id directly from WooCommerce before redirect
    if (typeof jQuery !== 'undefined') {
      jQuery(document).ajaxComplete((_event: any, xhr: any, settings: any) => {
        // Check if this is the checkout AJAX response
        if (settings.url && (
          settings.url.includes('wc-ajax=checkout') ||
          settings.url.includes('?wc-ajax=checkout')
        )) {
          this.log('Intercepted wc-ajax=checkout response');
          
          try {
            const response = JSON.parse(xhr.responseText);
            
            // WooCommerce returns { result: "success", order_id: 12345, redirect: "..." }
            if (response.result === 'success' && response.order_id) {
              const orderId = response.order_id.toString();
              
              if (isOrderTracked(orderId)) {
                this.log(`Order ${orderId} already tracked, skipping duplicate`);
                return;
              }
              
              this.log('Checkout successful, order_id:', orderId);
              
              // Mark as tracked BEFORE callback
              markOrderTracked(orderId);
              
              // Parse cart data from checkout page before redirect
              const cartData = this.parseCartFromCheckout();
              
              callback({
                orderId,
                orderNumber: orderId,
                orderValue: cartData?.total || 0,
                currency: this.detectCurrency(),
                items: cartData?.items || []
              });
            }
          } catch (error) {
            this.log('Failed to parse checkout response', error);
          }
        }
      });
      
      this.log('Installed wc-ajax=checkout interceptor');
    }

    // Method 2: Fallback - check if we're on order-received page
    // This handles cases where AJAX interception failed or page was refreshed
    const checkPurchasePage = () => {
      if (!document.body) return;
      
      const isOrderReceivedPage = 
        // Standard WooCommerce classes
        document.body.classList.contains('woocommerce-order-received') ||
        // Check for order confirmation elements
        !!document.querySelector('.woocommerce-order-received') ||
        !!document.querySelector('.woocommerce-order-overview') ||
        !!document.querySelector('.woocommerce-thankyou-order-received') ||
        !!document.querySelector('.woocommerce-order-details') ||
        // Check URL patterns
        /\/order-received\//.test(window.location.pathname) ||
        /\/checkout\/order-received\//.test(window.location.pathname) ||
        /\/zaprimljeno\//.test(window.location.pathname) || // Croatian order received
        /\/blagajna\/zaprimljeno\//.test(window.location.pathname) || // Croatian nested
        /[?&]key=wc_order_/.test(window.location.search);
      
      if (isOrderReceivedPage) {
        this.log('Order received page detected (fallback)');

        const orderData = this.extractOrderFromPage();
        if (orderData && orderData.orderId) {
          if (isOrderTracked(orderData.orderId)) {
            this.log(`Order ${orderData.orderId} already tracked via AJAX, skipping fallback`);
            return;
          }
          
          markOrderTracked(orderData.orderId);
          callback(orderData);
        }
      }
    };

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      checkPurchasePage();
    } else {
      document.addEventListener('DOMContentLoaded', checkPurchasePage);
    }
  }

  async getCart(): Promise<CartData | null> {
    // WooCommerce doesn't expose cart via API on frontend
    // We can try to parse it from mini-cart widget
    return this.parseCartFromDOM();
  }

  /**
   * Extract product data from single product page
   */
  private extractProductFromPage(): ProductData | null {
    try {
      const productElement = document.querySelector('.product, form.cart');
      if (!productElement) return null;

      const productId = 
        productElement.querySelector('[name="product_id"]')?.getAttribute('value') ||
        productElement.querySelector('[data-product_id]')?.getAttribute('data-product_id') ||
        '';

      const productName = 
        document.querySelector('.product_title, h1.product-title')?.textContent?.trim() || '';

      const priceElement = document.querySelector('.price .woocommerce-Price-amount, .price ins .amount');
      const productPrice = priceElement ? 
        parseFloat(priceElement.textContent?.replace(/[^0-9.]/g, '') || '0') : 0;

      const productSku = 
        document.querySelector('.sku')?.textContent?.trim() || '';

      return {
        productId,
        productSku,
        productName,
        productPrice,
        productUrl: window.location.href
      };
    } catch (error) {
      this.log('Failed to extract product from page', error);
      return null;
    }
  }

  /**
   * Extract product data from add-to-cart form
   */
  private extractProductFromForm(): ProductData | null {
    try {
      const form = document.querySelector('form.cart');
      if (!form) return null;

      const productId = form.querySelector('[name="product_id"]')?.getAttribute('value') || '';
      const quantity = parseInt(form.querySelector('[name="quantity"]')?.getAttribute('value') || '1');

      const productName = document.querySelector('.product_title')?.textContent?.trim() || '';
      const priceElement = document.querySelector('.price .woocommerce-Price-amount');
      const productPrice = priceElement ? 
        parseFloat(priceElement.textContent?.replace(/[^0-9.]/g, '') || '0') : 0;

      return {
        productId,
        productName,
        productPrice,
        quantity
      };
    } catch (error) {
      this.log('Failed to extract product from form', error);
      return null;
    }
  }

  /**
   * Extract order data from order received page
   */
  private extractOrderFromPage(): OrderData | null {
    try {
      // Extract order number
      const orderNumber = 
        document.querySelector('.woocommerce-order-overview__order strong, .order-number')?.textContent?.trim() || '';

      // Extract total
      const totalElement = document.querySelector('.woocommerce-order-overview__total strong, .order-total .amount');
      const orderValue = parsePrice(totalElement?.textContent || '0');

      // Extract order items
      const items: ProductData[] = [];
      document.querySelectorAll('.woocommerce-table--order-details tbody tr').forEach(row => {
        const nameElement = row.querySelector('.product-name');
        const qtyElement = row.querySelector('.product-quantity');
        const totalElement = row.querySelector('.product-total');

        if (nameElement) {
          items.push({
            productId: '',
            productName: nameElement.textContent?.trim() || '',
            quantity: parseInt(qtyElement?.textContent?.trim() || '1'),
            productPrice: parsePrice(totalElement?.textContent || '0')
          });
        }
      });

      return {
        orderId: orderNumber,
        orderNumber,
        orderValue,
        items
      };
    } catch (error) {
      this.log('Failed to extract order from page', error);
      return null;
    }
  }

  /**
   * Parse cart from checkout page order review table
   */
  private parseCartFromCheckout(): CartData | null {
    try {
      // Try checkout order review table
      const orderReview = document.querySelector('.woocommerce-checkout-review-order-table, #order_review, .shop_table');
      if (!orderReview) return null;

      const items: ProductData[] = [];
      orderReview.querySelectorAll('tbody tr.cart_item, tbody tr').forEach(row => {
        const nameElement = row.querySelector('.product-name, td.product-name');
        const totalElement = row.querySelector('.product-total, td.product-total');
        
        if (nameElement && !row.classList.contains('order-total')) {
          const nameText = nameElement.textContent?.trim() || '';
          // Extract quantity from name (e.g., "Product Name × 2")
          const qtyMatch = nameText.match(/×\s*(\d+)/);
          const quantity = qtyMatch ? parseInt(qtyMatch[1]) : 1;
          const productName = nameText.replace(/\s*×\s*\d+/, '').trim();
          
          items.push({
            productId: '',
            productName,
            quantity,
            productPrice: parsePrice(totalElement?.textContent || '0')
          });
        }
      });

      // Get order total
      const totalElement = orderReview.querySelector('.order-total td bdi, .order-total .amount, tfoot .order-total .amount') ||
                           document.querySelector('.order-total .woocommerce-Price-amount');
      const total = parsePrice(totalElement?.textContent || '0');

      // Get subtotal
      const subtotalElement = orderReview.querySelector('.cart-subtotal td bdi, .cart-subtotal .amount');
      const subtotal = parsePrice(subtotalElement?.textContent || total.toString());

      this.log('Parsed checkout cart:', { items: items.length, total, subtotal });

      return {
        items,
        subtotal,
        total
      };
    } catch (error) {
      this.log('Failed to parse cart from checkout', error);
      return null;
    }
  }

  /**
   * Parse cart from main cart page table
   */
  private parseCartFromCartPage(): CartData | null {
    try {
      // Look for the main cart table on the cart page
      const cartTable = document.querySelector('.woocommerce-cart-form table.cart, table.shop_table.cart, .woocommerce-cart table.shop_table');
      
      if (!cartTable) {
        this.log('No cart table found on cart page');
        return null;
      }

      const items: ProductData[] = [];
      cartTable.querySelectorAll('tbody tr.cart_item, tbody tr.woocommerce-cart-form__cart-item').forEach(row => {
        const nameElement = row.querySelector('.product-name a, td.product-name a');
        const qtyInput = row.querySelector('input.qty, input[name*="cart"][type="number"]') as HTMLInputElement;
        const priceElement = row.querySelector('.product-price .amount, td.product-price .amount');
        
        if (nameElement) {
          const productName = nameElement.textContent?.trim() || '';
          const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
          const price = parsePrice(priceElement?.textContent || '0');
          
          items.push({
            productId: '',
            productName,
            quantity,
            productPrice: price
          });
        }
      });

      // Get cart totals
      const totalElement = document.querySelector('.cart_totals .order-total td .amount, .cart-collaterals .order-total .amount, .woocommerce-cart-totals .order-total .amount') ||
                           document.querySelector('.cart_totals .woocommerce-Price-amount');
      const total = totalElement ? 
        parseFloat(totalElement.textContent?.replace(/[^0-9.,]/g, '').replace(',', '.') || '0') : 0;

      const subtotalElement = document.querySelector('.cart_totals .cart-subtotal td .amount, .cart-collaterals .cart-subtotal .amount');
      const subtotal = subtotalElement ?
        parseFloat(subtotalElement.textContent?.replace(/[^0-9.,]/g, '').replace(',', '.') || '0') : total;

      this.log('Parsed cart page:', { items: items.length, total, subtotal });

      return {
        items,
        subtotal,
        total
      };
    } catch (error) {
      this.log('Failed to parse cart from cart page', error);
      return null;
    }
  }

  /**
   * Parse cart from mini-cart widget
   */
  private parseCartFromDOM(): CartData | null {
    try {
      const cartWidget = document.querySelector('.widget_shopping_cart, .woocommerce-mini-cart');
      if (!cartWidget) return null;

      const items: ProductData[] = [];
      cartWidget.querySelectorAll('.mini_cart_item, .woocommerce-mini-cart-item').forEach(item => {
        const name = item.querySelector('.product-name, a')?.textContent?.trim() || '';
        const qtyElement = item.querySelector('.quantity');
        const quantity = qtyElement ? 
          parseInt(qtyElement.textContent?.match(/\d+/)?.[0] || '1') : 1;

        items.push({
          productId: '',
          productName: name,
          quantity
        });
      });

      const totalElement = cartWidget.querySelector('.woocommerce-mini-cart__total .amount, .total .amount');
      const total = totalElement ? 
        parseFloat(totalElement.textContent?.replace(/[^0-9.]/g, '') || '0') : 0;

      return {
        items,
        subtotal: total,
        total
      };
    } catch (error) {
      this.log('Failed to parse cart from DOM', error);
      return null;
    }
  }

  /**
   * Detect currency from the page
   */
  private detectCurrency(): string {
    try {
      // Try to find currency from WooCommerce price elements
      const priceElement = document.querySelector('.woocommerce-Price-currencySymbol');
      if (priceElement) {
        const symbol = priceElement.textContent?.trim();
        // Map common symbols to currency codes
        const currencyMap: Record<string, string> = {
          '€': 'EUR',
          '$': 'USD',
          '£': 'GBP',
          'kn': 'HRK',
          'HRK': 'HRK',
          'EUR': 'EUR'
        };
        if (symbol && currencyMap[symbol]) {
          return currencyMap[symbol];
        }
      }
      
      // Try to get from woocommerce_params if available
      if (typeof (window as any).woocommerce_params !== 'undefined') {
        const params = (window as any).woocommerce_params;
        if (params.currency) return params.currency;
      }
      
      return 'EUR'; // Default
    } catch {
      return 'EUR';
    }
  }
}
